import type { AdsOzetResponse, AdsRange } from "@/api/ads";
import { ChartCard } from "@/components/ChartCard";
import { EmptyState } from "@/components/EmptyState";
import { fmtX } from "@/features/ozet/format";
import { t } from "@/i18n";
import { fmtInt, fmtMoney } from "@/lib/utils";

import { AdsAralik } from "./AdsAralik";

const RENK_META = "#1877F2";
const RENK_GOOGLE = "#EA4335";

interface OrtakHavuzProps {
  data: AdsOzetResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  range: AdsRange;
  onRangeChange: (range: AdsRange) => void;
}

function MetricTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-card border-[1.5px] border-line bg-bg/60 p-3">
      <div className="kpi-lbl">{label}</div>
      <div className="kpi-val mt-1.5 text-[24px]">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

/** ChartCard pasta/doughnut DESTEKLEMEZ (bkz. components/ChartCard.tsx —
 * yalniz line/bar) — kaynak dagilimi bu yuzden basit bir CSS pay cubugu ile
 * gosterilir (gorev talimati: "yoksa basit SVG/CSS pay çubuğu yap"). */
function KaynakPayCubugu({ metaPay, googlePay }: { metaPay: number; googlePay: number }) {
  // Gorev madde 6: toplam harcama 0 iken cubuk tamamen bos (renksiz) kaliyor,
  // altindaki baslik da payi acmiyordu — bu durumda "veri yok" metni gosterilir.
  const veriYok = metaPay <= 0 && googlePay <= 0;
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-bg" role="img" aria-label={t("kpi.havuz.kaynak_dagilimi")}>
        {metaPay > 0 && <div style={{ width: `${metaPay}%`, backgroundColor: RENK_META }} />}
        {googlePay > 0 && <div style={{ width: `${googlePay}%`, backgroundColor: RENK_GOOGLE }} />}
      </div>
      {veriYok ? (
        <p className="mt-2 text-center text-xs text-muted">{t("kpi.havuz.kaynak_dagilimi_veri_yok")}</p>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted">
            <span className="inline-flex items-center gap-1">
              <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: RENK_META }} />
              {t("kpi.havuz.meta")} · %{metaPay.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
            </span>
            <span className="inline-flex items-center gap-1">
              <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: RENK_GOOGLE }} />
              {t("kpi.havuz.google")} · %{googlePay.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
            </span>
          </div>
          <p className="mt-2 text-center text-xs text-muted">{t("kpi.havuz.kaynak_dagilimi")}</p>
        </>
      )}
    </div>
  );
}

/**
 * "Reklam Performansı — Ortak Havuz" bölümü — legacy `kpi.html:58-102` +
 * `js/kpi.js` `renderAdsBand()` portu (madde A). Meta + Google Ads birleşik
 * 4 kart + kaynak dağılım payı + günlük harcama trendi. Google ağı henüz
 * senkron edilmediği için (bkz. app/core/sync/ads.py) her zaman 0/"—"
 * görünür — HATA DEĞİLDİR.
 */
export function OrtakHavuz({ data, isLoading, isError, range, onRangeChange }: OrtakHavuzProps) {
  const toplam = data?.toplam;
  const kaynaklar = data?.kaynaklar ?? [];
  const meta = kaynaklar.find((k) => k.network === "meta");
  const google = kaynaklar.find((k) => k.network === "google");
  const metaSpend = meta?.spend ?? 0;
  const googleSpend = google?.spend ?? 0;
  const toplamSpend = metaSpend + googleSpend;
  const metaPay = toplamSpend > 0 ? (metaSpend / toplamSpend) * 100 : 0;
  const googlePay = toplamSpend > 0 ? 100 - metaPay : 0;

  const gunluk = data?.gunluk ?? [];
  const googleGunlukVarMi = gunluk.some((g) => g.google_spend > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-[15px] font-bold text-ink">{t("kpi.havuz.baslik")}</h3>
        <AdsAralik value={range} onChange={onRangeChange} />
      </div>

      <div className="card" data-testid="kpi-reklam-havuz">
        {isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
        {isError && <EmptyState kind="error" />}
        {!isLoading && !isError && toplam && (
          <>
            <p className="mb-4 text-xs leading-relaxed text-muted">Maliyetler erişilebilen bağlı hesaplardan gelir. Google kaynağı GA4’tür. Eksik dönüşüm değeri veya birden fazla reklam ağının çakışan atıfları varsa birleşik satış ve ROAS gösterilmez.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MetricTile
                label={t("kpi.havuz.toplam_harcama")}
                value={kaynaklar.some(k => k.status !== "no_data") ? fmtMoney(toplam.spend) : "—"}
                sub={`${t("kpi.havuz.meta")} ${meta?.status === "no_data" ? "veri yok" : fmtMoney(metaSpend)} · ${t("kpi.havuz.google")} ${google?.status === "no_data" ? "veri yok" : fmtMoney(googleSpend)}`}
              />
              <MetricTile
                label={t("kpi.havuz.toplam_satis")}
                value={fmtMoney(toplam.conversion_value)}
                sub={`${fmtInt(toplam.conversions)} adet`}
              />
              <MetricTile label={t("kpi.havuz.roas")} value={fmtX(toplam.roas)} />
              <MetricTile label={t("kpi.havuz.tiklama")} value={fmtInt(toplam.clicks)} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
              <div className="flex flex-col justify-center">
                <KaynakPayCubugu metaPay={metaPay} googlePay={googlePay} />
              </div>
              <div>
                {gunluk.length > 0 ? (
                  <ChartCard
                    title={t("kpi.havuz.gunluk_trend")}
                    type="bar"
                    valueKind="money"
                    stacked
                    bare
                    height={200}
                    labelEvery={Math.max(1, Math.ceil(gunluk.length / 8))}
                    data={{
                      labels: gunluk.map((g) => g.day),
                      datasets: [
                        { label: t("kpi.havuz.meta"), data: gunluk.map((g) => g.meta_spend), backgroundColor: RENK_META },
                        ...(googleGunlukVarMi
                          ? [{ label: t("kpi.havuz.google"), data: gunluk.map((g) => g.google_spend), backgroundColor: RENK_GOOGLE }]
                          : []),
                      ],
                    }}
                  />
                ) : (
                  <EmptyState kind="no_data" />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
